import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";

export default function OrdenCompra() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const empresaId = localStorage.getItem("empresaId") || "1";
  const usuarioId = localStorage.getItem("userId") || "1"; // Ajustar según almacenamiento real

  const [formData, setFormData] = useState({
    proveedor: null,
    sucursal: null,
    fechaOrden: new Date().toISOString().split("T")[0],
    fechaEntrega: "",
    tipoCompra: "credito",
    notas: "",
  });

  const [detalles, setDetalles] = useState([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [precioUnitario, setPrecioUnitario] = useState(0);

  // Data from API
  const [proveedores, setProveedores] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCatalogos();
  }, []);

  const fetchCatalogos = async () => {
    try {
      const [provRes, invRes, sucRes] = await Promise.all([
        axios.get(API_ENDPOINTS.PROVEEDORES, { params: { empresa_id: empresaId } }),
        axios.get(API_ENDPOINTS.INVENTARIO.GENERAL, { params: { empresa_id: empresaId } }),
        axios.get(API_ENDPOINTS.SUCURSALES, { params: { empresa_id: empresaId } }),
      ]);
      setProveedores(provRes.data);
      setInventario(invRes.data);
      setSucursales(sucRes.data);

      // Default sucursal if only one
      if (sucRes.data.length > 0) {
        setFormData(prev => ({ ...prev, sucursal: sucRes.data[0] }));
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
    }
  };

  const agregarProducto = () => {
    if (!productoSeleccionado || cantidad <= 0 || precioUnitario < 0) {
      alert("Complete todos los campos del producto");
      return;
    }

    const nuevoDetalle = {
      id: Date.now(),
      inventario_id: productoSeleccionado.id, // ID del inventario requerido por backend
      producto: productoSeleccionado.producto || productoSeleccionado, // Manejar estructura anidada si existe
      cantidad,
      precioUnitario,
      subtotal: cantidad * precioUnitario,
    };

    setDetalles([...detalles, nuevoDetalle]);
    setProductoSeleccionado(null);
    setCantidad(1);
    setPrecioUnitario(0);
  };

  const eliminarProducto = (id) => {
    setDetalles(detalles.filter((d) => d.id !== id));
  };

  const calcularTotal = () => {
    return detalles.reduce((acc, det) => acc + det.subtotal, 0);
  };

  const guardarOrden = async () => {
    if (!formData.proveedor || detalles.length === 0 || !formData.sucursal) {
      alert("Seleccione una sucursal, un proveedor y agregue al menos un producto");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        empresa_id: empresaId,
        sucursal_id: formData.sucursal.id,
        proveedor_id: formData.proveedor.id,
        usuario_id: usuarioId,
        fecha_orden: formData.fechaOrden,
        fecha_entrega_estimada: formData.fechaEntrega,
        tipo_compra: formData.tipoCompra,
        notas: formData.notas,
        productos: detalles.map(d => ({
          inventario_id: d.inventario_id,
          cantidad: Number(d.cantidad),
          precio_unitario: Number(d.precioUnitario),
          descuento: 0
        }))
      };

      await axios.post(API_ENDPOINTS.COMPRAS, payload);
      alert("Orden de compra guardada exitosamente");

      // Limpiar formulario completo
      setFormData({
        proveedor: null,
        sucursal: sucursales.length > 0 ? sucursales[0] : null,
        fechaOrden: new Date().toISOString().split("T")[0],
        fechaEntrega: "",
        tipoCompra: "credito",
        notas: "",
      });
      setDetalles([]);
    } catch (error) {
      console.error("Error al guardar orden:", error);
      let msg = error.response?.data?.message || "Error al guardar orden";

      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        msg += "\n" + Object.keys(errors).map(key => `- ${errors[key].join(', ')}`).join('\n');
      } else if (error.response?.data?.error) {
        msg = error.response.data.error;
      }

      alert(`Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper para mostrar nombre del producto desde inventario
  const getProductoLabel = (option) => {
    if (option.producto) {
      return `${option.producto.codigo || ''} - ${option.producto.nombre}`;
    }
    return option.nombre || `Item ${option.id}`;
  };

  return (
    <MDBox>
      <Grid container spacing={3}>
        {/* Información General */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>info</Icon>
                Información de la Orden
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={sucursales}
                    getOptionLabel={(option) => option.nombre}
                    value={formData.sucursal}
                    onChange={(e, newValue) => setFormData({ ...formData, sucursal: newValue })}
                    renderInput={(params) => (
                      <TextField {...params} label="Sucursal *" variant="standard" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={proveedores}
                    getOptionLabel={(option) => option.nombre}
                    value={formData.proveedor}
                    onChange={(e, newValue) => setFormData({ ...formData, proveedor: newValue })}
                    renderInput={(params) => (
                      <TextField {...params} label="Proveedor *" variant="standard" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    type="date"
                    label="Fecha de Orden"
                    fullWidth
                    value={formData.fechaOrden}
                    onChange={(e) => setFormData({ ...formData, fechaOrden: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    type="date"
                    label="Fecha de Entrega Estimada"
                    fullWidth
                    value={formData.fechaEntrega}
                    onChange={(e) => setFormData({ ...formData, fechaEntrega: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: formData.fechaOrden }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Tipo de Compra"
                    fullWidth
                    variant="standard"
                    value={formData.tipoCompra}
                    onChange={(e) => setFormData({ ...formData, tipoCompra: e.target.value })}
                    SelectProps={{ native: true }}
                  >
                    <option value="contado">Contado</option>
                    <option value="credito">Crédito</option>
                    <option value="consignacion">Consignación</option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    label="Notas / Observaciones"
                    multiline
                    rows={3}
                    fullWidth
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>

        {/* Agregar Productos */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>add_circle</Icon>
                Agregar Productos
              </MDTypography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    options={inventario}
                    getOptionLabel={getProductoLabel}
                    value={productoSeleccionado}
                    onChange={(e, newValue) => {
                      setProductoSeleccionado(newValue);
                      // Autocomplete price if available
                      if (newValue && newValue.precio_venta) {
                        setPrecioUnitario(newValue.precio_venta); // Or cost?
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Producto (Inventario) *" variant="standard" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDInput
                    type="number"
                    label="Cantidad"
                    fullWidth
                    value={cantidad}
                    onChange={(e) => setCantidad(Number(e.target.value))}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    type="number"
                    label="Costo Unitario"
                    fullWidth
                    value={precioUnitario}
                    onChange={(e) => setPrecioUnitario(Number(e.target.value))}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant="gradient"
                    color={sidenavColor}
                    fullWidth
                    onClick={agregarProducto}
                  >
                    <Icon sx={{ mr: 1 }}>add</Icon>
                    Agregar
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>

        {/* Tabla de Productos */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>
                <Icon sx={{ mr: 1 }}>shopping_basket</Icon>
                Productos en la Orden
              </MDTypography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Costo Unit.</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <MDTypography variant="body2" color="text">
                            No hay productos agregados
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      detalles.map((detalle) => (
                        <TableRow key={detalle.id}>
                          <TableCell>
                            {detalle.producto?.nombre || detalle.producto?.producto?.nombre || "Producto"}
                          </TableCell>
                          <TableCell align="right">{detalle.cantidad}</TableCell>
                          <TableCell align="right">${Number(detalle.precioUnitario).toFixed(2)}</TableCell>
                          <TableCell align="right">${Number(detalle.subtotal).toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => eliminarProducto(detalle.id)}
                            >
                              <Icon>delete</Icon>
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {detalles.length > 0 && (
                <MDBox mt={3} display="flex" justifyContent="flex-end" alignItems="center">
                  <MDTypography variant="h5" mr={2}>
                    Total Estimado:
                  </MDTypography>
                  <MDTypography variant="h4" color={sidenavColor}>
                    ${calcularTotal().toFixed(2)}
                  </MDTypography>
                </MDBox>
              )}

              <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton variant="outlined" color="secondary" onClick={() => window.history.back()}>
                  Cancelar
                </MDButton>
                <MDButton
                  variant="gradient"
                  color={sidenavColor}
                  onClick={guardarOrden}
                  disabled={loading}
                >
                  {loading ? <Icon sx={{ animation: 'spin 2s linear infinite' }}>refresh</Icon> : <Icon sx={{ mr: 1 }}>save</Icon>}
                  {loading ? "Guardando..." : "Guardar Orden"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
